/**
 * The line under Reject Qty (QUALITY-NCR-REVIEW Decision 3).
 *
 * With a goods receipt linked it shows what was delivered and what is left to
 * reject; the server refuses more than that on save. With no GRN linked it
 * shows only a warning when Reject Qty is more than Batch Qty -- the server
 * still caps a GRN number typed against a PO line (round 2).
 */
import { useEffect, useState } from 'react';
import { qualityAPI } from '../../lib/api';
import type { RejectAvailability } from '../../lib/api/quality';

export interface RejectQtyHintProps {
  /** The linked goods receipt, or null when none is linked. */
  grnId: number | null | undefined;
  /** The NCR being edited, so its own quantity is not counted as "elsewhere". */
  ncrId?: number;
  rejectQty: number | string | null | undefined;
  batchQty: number | string | null | undefined;
}

const toNumber = (v: number | string | null | undefined): number => {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Delivered / left-to-reject hint for the NCR's Reject Qty input.
 *
 * @param props - {@link RejectQtyHintProps}
 * @returns the hint line, or nothing when there is nothing to say
 */
export default function RejectQtyHint({ grnId, ncrId, rejectQty, batchQty }: RejectQtyHintProps) {
  const [avail, setAvail] = useState<RejectAvailability | null>(null);

  useEffect(() => {
    let live = true;
    setAvail(null);
    if (!grnId) return;
    qualityAPI.getRejectAvailability(grnId, ncrId)
      .then(res => { if (live) setAvail(res.data); })
      .catch((err: unknown) => { console.error('Reject availability lookup failed:', err); });
    return () => { live = false; };
  }, [grnId, ncrId]);

  const reject = toNumber(rejectQty);
  const batch = toNumber(batchQty);

  if (grnId && avail && avail.delivered !== null) {
    const left = avail.available ?? 0;
    const over = reject > left;
    return (
      <p className={`text-xs mt-1 tabular-nums ${over ? 'text-red-600' : 'text-muted-foreground'}`} role={over ? 'alert' : undefined}>
        Delivered on this GRN: {avail.delivered}
        {avail.rejected_elsewhere ? ` · ${avail.rejected_elsewhere} already on other NCRs` : ''}
        {over ? ` · only ${left} left to reject` : ''}
      </p>
    );
  }
  if (!grnId && batch > 0 && reject > batch) {
    return <p className="text-xs mt-1 text-amber-700">Reject Qty is more than Batch Qty.</p>;
  }
  return null;
}
